import { Box, Flex, Typography } from "@aircall/tractor";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import { useEvaluation } from "@/hooks/useEvaluation/useEvaluation";
import { useEvaluationPercentage } from "@/hooks/useEvaluationPercentage/useEvaluationPercentage";
import { SectionHeader } from "../SectionHeader";
import { SectionLayout } from "../SectionLayout";

import { StyledButton } from "./SectionCallEvaluation.styled";

import { Average } from "@/components/Average/Average";
import { EvaluationFragment } from "@/graphql/generated/graphql";
import { useCall } from "@/hooks/useCall/useCall";
import { useHasCcAccess } from "@/hooks/useHasCcAccess/useHasCcAccess";
import { useBaseStore } from "@/store/store";

function NoEvaluation() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const setEvaluationTabSource = useBaseStore(
    (store) => store.setEvaluationTabSource
  );
  const setShouldTrackEvaluationTabSource = useBaseStore(
    (store) => store.setShouldTrackEvaluationTabSource
  );

  const hasCcAccess = useHasCcAccess();

  const startCallEvaluationHandler = () => {
    setEvaluationTabSource("insights_cta");
    setShouldTrackEvaluationTabSource(true);
    navigate("../evaluation");
  };

  return (
    <Flex flexDirection="column" gap="xxs">
      <SectionHeader>{t("call_context.call_evaluation")}</SectionHeader>
      {hasCcAccess ? (
        <Box>
          <StyledButton
            data-test="start-call-evaluation-button-AP"
            onClick={startCallEvaluationHandler}
            variant="secondary"
            mode="link"
            size="xSmall"
          >
            {t("call_context.start_call_evaluation")}
          </StyledButton>
        </Box>
      ) : (
        <Typography variant="bodyRegularS" color="text-disabled">
          {t("call_context.no_call_evaluation")}
        </Typography>
      )}
    </Flex>
  );
}

type WithEvaluationProps = {
  evaluation: EvaluationFragment;
};

function WithEvaluation({ evaluation }: Readonly<WithEvaluationProps>) {
  const { t } = useTranslation();

  const { score, feedback } = evaluation;

  const { percentage } = useEvaluationPercentage({
    points: score.scorePoints,
    maximumPoints: score.totalPoints,
  });

  return (
    <Flex flexDirection="column" gap="xs">
      <Flex justifyContent="space-between" alignItems="center">
        <Flex flexDirection="column" gap="xxxs">
          <SectionHeader>{t("call_context.call_evaluation")}</SectionHeader>
          <Typography variant="supportingRegularS">
            {t("evaluation.overall_score", {
              score: score.scorePoints,
              maxScore: score.totalPoints,
            })}
          </Typography>
        </Flex>
        <Average value={percentage} borderless />
      </Flex>
      <Typography variant="bodyRegularM">{feedback}</Typography>
    </Flex>
  );
}

function PickContent() {
  const { callId } = useParams();

  const { evaluation, loading } = useEvaluation(callId);

  if (loading) {
    return null;
  }

  if (!evaluation) {
    return <NoEvaluation />;
  }

  return <WithEvaluation evaluation={evaluation} />;
}

export function SectionCallEvaluation() {
  const { call } = useCall();

  if (!call) {
    return null;
  }

  return (
    <SectionLayout data-test="call-evaluation-section">
      <PickContent />
    </SectionLayout>
  );
}
